import { useState, useEffect, useCallback, useRef } from 'react';

const useResizable = ({ minPercent, maxPercent, defaultPercent, storageKey }) => {
    const [widthPercent, setWidthPercent] = useState(() => {
        const savedWidth = localStorage.getItem(storageKey);
        if (savedWidth) {
            const parsedWidth = parseFloat(savedWidth);
            // Validate that the saved value is a reasonable percentage (0-100)
            // If it's outside this range, it's likely an old pixel value
            if (parsedWidth >= 0 && parsedWidth <= 100) {
                return parsedWidth;
            }
        }
        return defaultPercent;
    });
    const [isDragging, setIsDragging] = useState(false);
    const elementRef = useRef(null);

    const handleMouseDown = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback(
        e => {
            if (!isDragging || !elementRef.current) return;

            const parentWidth = elementRef.current.parentElement.offsetWidth;
            const elementRect = elementRef.current.getBoundingClientRect();
            const newWidthPixels = elementRect.right - e.clientX;
            const newWidthPercent = (newWidthPixels / parentWidth) * 100;
            const constrainedWidth = Math.min(Math.max(newWidthPercent, minPercent), maxPercent);

            setWidthPercent(constrainedWidth);
        },
        [isDragging, minPercent, maxPercent]
    );

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            localStorage.setItem(storageKey, widthPercent.toString());
        }
    }, [isDragging, widthPercent, storageKey]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return {
        widthPercent,
        elementRef,
        handleMouseDown,
        isDragging,
    };
};

export default useResizable;
